import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { KeyRound, Loader2 } from "lucide-react";

export function ChangePasswordDialog({ open, onOpenChange }) {
  const { changePassword } = useAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const valid = next.length >= 10 && /[A-Z]/.test(next) && /[a-z]/.test(next) && /\d/.test(next);

  const submit = async () => {
    if (next !== confirm) { toast.error("New passwords don't match"); return; }
    if (!valid) { toast.error("Password needs 10+ chars with upper, lower & a number"); return; }
    setBusy(true);
    try {
      await changePassword(cur, next);
      toast.success("Password updated");
      setCur(""); setNext(""); setConfirm("");
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change password");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2">
            <KeyRound size={18} className="text-cyan-600" /> Change password
          </DialogTitle>
          <DialogDescription>Update your sign-in password. You'll stay logged in on this device.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="stamp-label text-slate-600">Current password</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} className="mt-1 rounded-sm h-10" data-testid="current-password-input" autoComplete="current-password" />
          </div>
          <div>
            <Label className="stamp-label text-slate-600">New password</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} className="mt-1 rounded-sm h-10" data-testid="new-password-input" autoComplete="new-password" />
            <p className={`text-[11px] mt-1 ${valid ? "text-emerald-600" : "text-slate-400"}`}>At least 10 characters with an uppercase letter, lowercase letter and a number.</p>
          </div>
          <div>
            <Label className="stamp-label text-slate-600">Confirm new password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 rounded-sm h-10" data-testid="confirm-password-input" autoComplete="new-password" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={busy || !cur || !next} data-testid="save-password-btn" className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white">
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : <KeyRound size={16} className="mr-2" />} Update password
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
